import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiFetch, apiFetchEnvelope } from "@/lib/api-client";
import type { BrandAsset } from "./use-brand-assets";

export interface AssetFilters {
  type?: string | null;
  projectId?: string | null;
  q?: string;
  tags?: string[];
}

interface AssetPage {
  items: BrandAsset[];
  nextCursor: string | null;
}

/** Build the query string for the paginated asset list (pure; exported for tests). */
export function buildAssetsQuery(filters: AssetFilters, cursor?: string): string {
  const p = new URLSearchParams();
  if (filters.type) p.set("type", filters.type);
  if (filters.projectId) p.set("projectId", filters.projectId);
  if (filters.q && filters.q.trim()) p.set("q", filters.q.trim());
  if (filters.tags && filters.tags.length > 0) p.set("tags", filters.tags.join(","));
  if (cursor) p.set("cursor", cursor);
  return p.toString();
}

// Use apiFetchEnvelope (not apiFetch): the list response is { data, nextCursor }
// and apiFetch unwraps `.data`, which would drop the cursor. The envelope
// variant still runs the shared credentials/error/401-redirect handling.
async function fetchAssetPage(
  brandId: string,
  filters: AssetFilters,
  cursor?: string,
): Promise<AssetPage> {
  const qs = buildAssetsQuery(filters, cursor);
  const json = await apiFetchEnvelope<{ data: BrandAsset[]; nextCursor: string | null }>(
    `/brands/${brandId}/assets${qs ? "?" + qs : ""}`,
  );
  return { items: json.data ?? [], nextCursor: json.nextCursor ?? null };
}

export function useLibraryAssets(brandId: string, filters: AssetFilters) {
  return useInfiniteQuery({
    queryKey: [
      "library-assets",
      brandId,
      filters.type ?? "",
      filters.projectId ?? "",
      filters.q ?? "",
      (filters.tags ?? []).join(","),
    ],
    queryFn: ({ pageParam }) => fetchAssetPage(brandId, filters, pageParam as string | undefined),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.nextCursor ?? undefined,
    enabled: !!brandId,
    staleTime: 15_000,
  });
}

export function useBrandAssetTags(brandId: string) {
  return useQuery({
    queryKey: ["brand-asset-tags", brandId],
    queryFn: () => apiFetch<string[]>(`/brands/${brandId}/asset-tags`),
    enabled: !!brandId,
    staleTime: 60_000,
  });
}

export function useUpdateAssetTags(brandId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ assetId, tags }: { assetId: string; tags: string[] }) =>
      apiFetch<BrandAsset>(`/brands/${brandId}/assets/${assetId}/tags`, {
        method: "PUT",
        body: JSON.stringify({ tags }),
      }),
    // Optimistically patch the asset's tags in the paginated cache so the
    // controlled TagInput doesn't flicker back to the old value between the
    // mutation and its refetch, and so rapid successive edits build on the
    // latest tags rather than a stale base (avoids lost tags).
    onMutate: async ({ assetId, tags }) => {
      await qc.cancelQueries({ queryKey: ["library-assets", brandId] });
      const prev = qc.getQueriesData({ queryKey: ["library-assets", brandId] });
      qc.setQueriesData({ queryKey: ["library-assets", brandId] }, (old: any) => {
        if (!old?.pages) return old;
        return {
          ...old,
          pages: old.pages.map((page: any) => ({
            ...page,
            items: (page.items as BrandAsset[]).map((a) =>
              a.id === assetId ? { ...a, tags } : a,
            ),
          })),
        };
      });
      return { prev };
    },
    onError: (_err, _vars, ctx) => {
      for (const [key, data] of ctx?.prev ?? []) qc.setQueryData(key, data);
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: ["library-assets", brandId] });
      qc.invalidateQueries({ queryKey: ["brand-asset-tags", brandId] });
      qc.invalidateQueries({ queryKey: ["brand-assets", brandId] });
    },
  });
}
